import { apiUrl } from "./base";
import type { SessionMetadata } from "../utils/sessionMetadata";
const rankingIntakeUrl = import.meta.env.VITE_RANKING_INTAKE_URL;

export interface SessionAPI {
  id: string;
  title: string;
  contactType: string;
  status: "draft" | "in_progress" | "completed";
  createdAt: string;
  updatedAt?: string;
  startTime?: string;
  endTime?: string;
  duration: number;
  sourceLanguage?: string;
  targetLanguage?: string;
  languageDetected: string | null;
  participantCount: number;
  preview: string;
  summary?: string; // latest meeting insights summary (if available)
  metadata?: SessionMetadata;
  caseId?: string; // Internal database ID
  case_number?: string; // User-visible case number
  respondentName?: string;
  interviewerName?: string;
  likelihood?: number;
  narrative?: string;
  alarms?: string;
  screeningDecision?: string;
  maltreatment?: string;
  llmPredictionExplanation?: string;
  priorityRanking?: number;
  offenderRole?: string;
  victimType?: string;
  sourceType?: string; // Type of data source (live_recording, wav_file, mp3_file, etc.)
  hasRecording?: boolean; // Whether the session has an audio recording
}



export interface CreateSessionRequest {
  title: string;
  contact_type: string;
  source_language: string;
  target_language?: string;
  status?: "draft" | "in_progress" | "completed";
  metadata?: Partial<SessionMetadata>;
  case_number?: string;
  respondent_name?: string;
  interviewer_name?: string;
  likelihood?: number | undefined;
}

export interface UpdateSessionRequest {
  title?: string;
  status?: "draft" | "in_progress" | "completed";
  contact_type?: string;
  start_time?: Date;
  end_time?: Date;
  duration_seconds?: number;
  source_language?: string;
  target_language?: string;
  language_detected?: string;
  participant_count?: number;
  preview?: string;
  metadata?: Partial<SessionMetadata>;
  respondent_name?: string;
  interviewer_name?: string;
  case_number?: string;
   // Changed from case_id to case_number to match API
  likelihood?: number | undefined;
  source_type?: string; // Type of data source (live_recording, wav_file, mp3_file, etc.)
}

export interface SessionsResponse {
  sessions: SessionAPI[];
  count: number;
}

export async function listSessions(filters?: {
  status?: string;
  contact_type?: string;
  search?: string;
  limit?: number;
  respondent?: string;
  case_number?: string;
  date_from?: string;
  date_to?: string;
}): Promise<SessionsResponse> {
  const params = new URLSearchParams();

  if (filters?.status && filters.status !== "all") {
    params.append("status", filters.status);
  }

  if (filters?.contact_type && filters.contact_type !== "all") {
    params.append("contact_type", filters.contact_type);
  }

  if (filters?.search) {
    params.append("search", filters.search);
  }

  if (filters?.respondent) {
    params.append("respondent", filters.respondent);
  }

  if (filters?.case_number) {
    params.append("case_number", filters.case_number);
  }

  if (filters?.date_from) {
    params.append("date_from", filters.date_from);
  }

  if (filters?.date_to) {
    params.append("date_to", filters.date_to);
  }

  if (filters?.limit) {
    params.append("limit", filters.limit.toString());
  }

  const query = params.toString();
  const response = await fetch(
    apiUrl(query ? `/api/sessions?${query}` : "/api/sessions")
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch sessions: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Get a single session by ID
 */
export async function getSession(id: string): Promise<SessionAPI> {
  const response = await fetch(apiUrl(`/api/sessions/${id}`));

  if (!response.ok) {
    throw new Error(`Failed to fetch session: ${response.statusText}`);
  }

  return await response.json();
}

export async function triggerIntakeRanking(sessionId: string): Promise<void> {
  const fullUrl = `${rankingIntakeUrl}pipeline/run/${sessionId}`;
  console.log("[triggerIntakeRanking] Starting request");
  console.log("[triggerIntakeRanking] Base URL:", rankingIntakeUrl);
  console.log("[triggerIntakeRanking] Session ID:", sessionId);
  console.log("[triggerIntakeRanking] Full URL:", fullUrl);
  
  try {
    const response = await fetch(fullUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
    
    console.log("[triggerIntakeRanking] Response status:", response.status);
    console.log("[triggerIntakeRanking] Response ok:", response.ok);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("[triggerIntakeRanking] Error response body:", errorText);
      throw new Error(`Failed to trigger intake ranking: ${response.statusText} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log("[triggerIntakeRanking] Success response:", result);
    return result;
  } catch (err) {
    console.error("[triggerIntakeRanking] Exception occurred:", err);
    throw err;
  }
}
/**
 * Create a new session
 */
export async function createSession(
  data: CreateSessionRequest
): Promise<SessionAPI> {
  const response = await fetch(apiUrl("/api/sessions"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create session: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Update an existing session
 */
export async function updateSession(
  sessionId: string,
  updates: UpdateSessionRequest
): Promise<SessionAPI> {
  const response = await fetch(
    apiUrl(`/api/sessions/${sessionId}`),
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update session: ${response.statusText}`);
  }

  console.log("response updateSession.ts", response);
  return await response.json();
}

/**
 * Delete a session
 */
export async function deleteSession(id: string): Promise<void> {
  const response = await fetch(apiUrl(`/api/sessions/${id}`), {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error(`Failed to delete session: ${response.statusText}`);
  }
}
